import type {
  ActivityFeedResponse,
  CarePlanResponse,
  CareTeamMember,
  GenerateOtcResponse,
  GetPresignedUrlForGetPayload,
  GetPresignedUrlForGetResponse,
  GetPresignedUrlForPostPayload,
  GetPresignedUrlForPostResponse,
  Note,
  PatientDetailsResponse,
  PatientDiagnosis,
} from "@/core/types";
import type { ApiContext } from "@/api/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";
import type { PatientFile } from "@/features/patient/types/PatientFile";

export const sharedRequestsTyped = {
  fetch_patient_careteam: function ({
    context,
    patientId,
    searchParams = {},
  }: {
    context?: ApiContext;
    patientId: string;
    searchParams?: Record<string, string>;
  }): Promise<CareTeamMember[]> {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/care_team`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams,
      })
      .json<CareTeamMember[]>();
  },

  fetch_patient_details: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<PatientDetailsResponse> {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/patient_details`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientDetailsResponse>();
  },

  generate_otc: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<GenerateOtcResponse> {
    return httpAuth2
      .post("/v1/auth/generate_otc", {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        searchParams: { patient_id: patientId },
      })
      .json<GenerateOtcResponse>();
  },

  get_activity_feed: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<ActivityFeedResponse> {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/activity`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<ActivityFeedResponse>();
  },

  get_care_plan: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<CarePlanResponse> {
    return httpAuth2
      .post(`/v1/care-plan/${patientId}/get_care_plan`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<CarePlanResponse>();
  },

  get_files: function ({
    data,
  }: {
    data: { entity_id: string; entity_type: string };
  }): Promise<PatientFile[]> {
    return httpAuth2
      .post("/v1/provider/get_files", {
        context: { showLoader: false },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json<PatientFile[]>();
  },

  get_notes_summary: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<Note[]> {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/get_notes_summary`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<Note[]>();
  },

  get_patient_diagnosis: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<PatientDiagnosis> {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/get_patient_diagnosis`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<PatientDiagnosis>();
  },

  get_presigned_url: function ({
    data,
    version = "v4",
  }: {
    data: GetPresignedUrlForGetPayload;
    version?: string;
  }): Promise<GetPresignedUrlForGetResponse> {
    return httpAuth2
      .post(`/${version}/provider/get_presigned_url`, {
        context: { showLoader: false },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json<GetPresignedUrlForGetResponse>();
  },

  post_presigned_url: function ({
    data,
    version = "v4",
  }: {
    data: GetPresignedUrlForPostPayload;
    version?: string;
  }): Promise<GetPresignedUrlForPostResponse> {
    return httpAuth2
      .post(`/${version}/provider/get_presigned_url`, {
        context: { showLoader: false },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: data,
      })
      .json<GetPresignedUrlForPostResponse>();
  },

  send_on_demand_welcome_email: function ({
    context,
    patientId,
  }: {
    context?: ApiContext;
    patientId: string;
  }): Promise<void> {
    return httpAuth2
      .post(`/v1/patient_management/${patientId}/send_on_demand_welcome_email`, {
        context,
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<void>();
  },
};
